import { useSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useGetOrganisation, useUpdateOrganisation } from '../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../app/CurrentOrganisationProvider';
import { SwitchInput, SwitchValue } from '../../../../../components/CustomFields/Switch';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { Form } from '../../../../../components/Form';
import { Card, Loading, type OnSubmit, type ValueComponentProps } from '../../../../../design-system';
import { fullStateSelector } from '../../../../../store/root';
import type { SubState } from '../../../../../store/types';
import { createSchema, type FormFields } from '../../../../../utils/formUtils';
import { normalise } from '../../../../../utils/request';

type FormOfflineChargingSettings = {
  allowLongTermOfflineCharging: boolean;
};

const validationSchema = createSchema<FormOfflineChargingSettings>({
  allowLongTermOfflineCharging: Yup.boolean().required(),
});

const formFields: FormFields<FormOfflineChargingSettings> = {
  allowLongTermOfflineCharging: {
    id: 'allowLongTermOfflineCharging',
    label: 'Allow long term offline charging',
    inputComponent: SwitchInput,
    valueComponent: ({ value }: ValueComponentProps<boolean>) => (
      <SwitchValue value={value} labels={{ true: 'Enabled', false: 'Disabled' }} />
    ),
  },
};

const FetchContext: React.FC<{ organisation: SubState<'organisation'> }> = ({ organisation }) => {
  const { mutate: updateOrg } = useUpdateOrganisation({ orgId: organisation.id });

  const { data, error, loading, refetch } = useGetOrganisation({
    orgSlug: organisation.slug,
  });
  const organisationData = data ? normalise(data) : undefined;

  const { isEvnexAdmin } = useSelector(fullStateSelector);

  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const initialValues: FormOfflineChargingSettings = {
    allowLongTermOfflineCharging: organisationData?.allowLongTermOfflineCharging ?? true,
  };

  const onSave = React.useCallback<OnSubmit<FormOfflineChargingSettings>>(
    async (formValues) => {
      try {
        await updateOrg({
          data: {
            attributes: {
              allowLongTermOfflineCharging: formValues.allowLongTermOfflineCharging,
            },
            type: 'organisations',
            id: organisation.id,
          },
        });
        enqueueSnackbar('Offline charging settings updated successfully', { variant: 'success' });
        setIsEditing(false);
        await refetch();
      } catch (e) {
        errorSnackbar('Error: save failed, please try again later', closeSnackbar, enqueueSnackbar, e);
      }
    },
    [organisation.id, updateOrg, enqueueSnackbar, closeSnackbar, refetch],
  );

  if (error) {
    return (
      <DataErrorHandler
        description={
          error.status === 403 ? 'Not authorized to view this content' : 'Unable to load offline charging settings'
        }
        error={error}
        refetch={refetch}
        type="embedded"
      />
    );
  }

  return (
    <Card title="Offline Charging Settings">
      <Form<FormOfflineChargingSettings>
        fields={formFields}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSave}
        isLoading={loading}
        isEditing={isEvnexAdmin ? isEditing : false}
        setIsEditing={isEvnexAdmin ? setIsEditing : undefined}
      />
    </Card>
  );
};

export const OfflineChargingSettings: React.FC = () => {
  const { organisation, loading } = useCurrentOrganisation();

  if (loading || !organisation) {
    return <Loading />;
  }

  return <FetchContext organisation={organisation} />;
};
